import React, { useState } from "react";
import { MapPin, Phone, Mail, Instagram, Send, AlertCircle, Check } from "lucide-react";
import { SITE_INFO } from "../mock";
import { sendMessage } from "../api";
import { useToast } from "../hooks/use-toast";

export default function Contact() {
  const { toast } = useToast();
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await sendMessage(form);
      setSent(true);
      toast({ title: "Message envoyé !", description: "Ludovic vous répondra dans les plus brefs délais." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast({ title: "Erreur d'envoi", description: err.response?.data?.detail || "Réessayez plus tard", variant: "destructive" });
    }
    setSubmitting(false);
  };

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-24">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16 fade-in">
          <p className="text-gold text-sm tracking-[0.3em] uppercase mb-4">Prendre contact</p>
          <h1 className="text-5xl md:text-6xl mb-6 section-title"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Contact
          </h1>
          <div className="gold-divider mb-6" />
          <p className="text-gray-400">À votre écoute pour donner vie à vos projets.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Info */}
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl mb-6"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                Mes coordonnées
              </h2>
              <div className="space-y-5">
                <InfoRow Icon={MapPin} label="Localisation">
                  {SITE_INFO.location}, France<br/>
                  <span className="text-sm text-gray-400">Déplacements dans toute la France</span>
                </InfoRow>
                <InfoRow Icon={Phone} label="Téléphone / Wero">
                  <a href={`tel:${SITE_INFO.phone.replace(/\s/g, '')}`} className="hover:text-gold transition-colors">
                    {SITE_INFO.phone}
                  </a>
                </InfoRow>
                <InfoRow Icon={Mail} label="Email">
                  <a href={`mailto:${SITE_INFO.email}`} className="hover:text-gold transition-colors break-all">
                    {SITE_INFO.email}
                  </a>
                </InfoRow>
              </div>
            </div>

            <div>
              <h3 className="text-gold text-xs uppercase tracking-widest mb-4">Réseaux sociaux</h3>
              <div className="space-y-2 text-sm text-gray-300">
                <p><strong className="text-white">Instagram :</strong> {SITE_INFO.social.instagramHandle}</p>
                <p><strong className="text-white">Snap :</strong> {SITE_INFO.social.snapchat}</p>
                <p><strong className="text-white">TikTok :</strong> {SITE_INFO.social.tiktokHandle}</p>
                <p><strong className="text-white">Facebook :</strong> {SITE_INFO.social.facebook}</p>
                <p><strong className="text-white">Page Facebook :</strong> {SITE_INFO.social.facebookPage}</p>
                <p><strong className="text-white">Google :</strong> {SITE_INFO.social.google}</p>
              </div>
              <a
                href={SITE_INFO.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold mt-6 inline-flex text-xs mr-2"
              >
                <Instagram size={14} />
                Instagram
              </a>
              <a
                href={`https://www.snapchat.com/add/${SITE_INFO.social.snapchat}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold mt-6 inline-flex text-xs"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.078-.54.078-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.074.42.194.789.3 1.104.3.234 0 .384-.06.465-.105l-.046-.569c-.098-1.626-.225-3.651.307-4.837C7.392 1.077 10.739.807 11.727.807l.419-.015h.06z"/>
                </svg>
                Snapchat
              </a>
            </div>

            <div className="border border-gold/30 bg-gold/5 p-5 flex items-start gap-3">
              <AlertCircle size={18} className="text-gold shrink-0 mt-0.5" />
              <p className="text-xs text-gray-300 leading-relaxed">
                <strong className="text-white">Info importante :</strong> les frais de déplacement (carburant, péage) sont à la charge du client pour toute prestation hors de Fos Sur Mer.
              </p>
            </div>
          </div>

          {/* Form */}
          <div>
            <div className="border border-white/10 bg-zinc-950/40 p-6 md:p-8">
              <h2 className="text-2xl mb-6"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                M'envoyer un message
              </h2>

              {sent ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 rounded-full border-2 border-gold mx-auto mb-4 flex items-center justify-center">
                    <Check size={28} className="text-gold" />
                  </div>
                  <p className="text-2xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Message envoyé !
                  </p>
                  <p className="text-gray-400 text-sm mb-6">Vous recevrez une réponse rapidement.</p>
                  <button onClick={() => setSent(false)} className="btn-gold text-xs">Envoyer un autre message</button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <CField label="Nom" value={form.name} onChange={(v) => setForm({...form, name: v})} required />
                  <CField label="Email" type="email" value={form.email} onChange={(v) => setForm({...form, email: v})} required />
                  <CField label="Téléphone" type="tel" value={form.phone} onChange={(v) => setForm({...form, phone: v})} />
                  <CField label="Sujet" value={form.subject} onChange={(v) => setForm({...form, subject: v})} />
                  <div>
                    <label className="block text-xs tracking-wider uppercase text-gray-400 mb-2">Message *</label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({...form, message: e.target.value})}
                      rows={5}
                      required
                      className="w-full bg-black border border-white/20 focus:border-gold focus:outline-none p-3 text-white"
                    />
                  </div>
                  <button type="submit" disabled={submitting} className="btn-gold-filled w-full justify-center disabled:opacity-50">
                    <Send size={16} />
                    {submitting ? "Envoi..." : "Envoyer"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ Icon, label, children }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 border border-gold/40 flex items-center justify-center shrink-0">
        <Icon size={16} className="text-gold" />
      </div>
      <div>
        <p className="text-gold text-xs uppercase tracking-widest mb-1">{label}</p>
        <p className="text-white">{children}</p>
      </div>
    </div>
  );
}

function CField({ label, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="block text-xs tracking-wider uppercase text-gray-400 mb-2">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-black border border-white/20 focus:border-gold focus:outline-none p-3 text-white"
      />
    </div>
  );
}
